<template>
  <div id="app" :class="{ 'has-env-banner': envBanner, 'is-offline': isOffline }">
    <div v-show="envBanner" class="env-banner">
      This is a demo and under development — not for production use
    </div>
    <AppHeader :offline="isOffline" />
    <main>
      <!-- Site offline (tenant-config `offline`): one palette-independent notice
           instead of the page; header, footer, legal modals and the ?cancel= flow
           keep working. Nothing that could start a booking is rendered. -->
      <OfflinePage v-if="isOffline" :businessName="businessName" :message="offlineMessage" />
      <template v-else>
        <HeroSection
          :businessName="businessName"
          :businessMotto="businessMotto"
          :showHeroText="showHeroText"
          :showLogo="showLogo"
        />
        <div class="reveal-wrap"><ServicesSection /><TrustStrip /></div>
        <div class="reveal-wrap"><AtmosphereSection /></div>
        <div class="reveal-wrap"><VisitUsSection /></div>
        <div class="reveal-wrap"><FaqSection /></div>
      </template>
    </main>
    <div class="reveal-wrap">
      <AppFooter
        @show-impressum="showImpressum = true"
        @show-data-protection="showDataProtection = true"
        @show-terms="showTerms = true"
      />
    </div>
    
    <!-- Legal Modals -->
    <ImpressumModal v-if="showImpressum" @close="showImpressum = false" />
    <DataProtectionModal v-if="showDataProtection" @close="showDataProtection = false" />
    <TermsModal v-if="showTerms" @close="showTerms = false" />
    
    <!-- Announcement Modal (not while offline: the offline page is the notice) -->
    <AnnouncementModal v-if="!isOffline" @resolved="onAnnouncementResolved" @closed="onAnnouncementClosed" />
    
    <!-- Cancellation Modal -->
    <CancellationModal
      :isVisible="showCancellationModal"
      :bookingId="cancellationBookingId"
      @close="closeCancellationModal"
    />

    <!-- Booking sheet (single instance, opened from anywhere via useBookingSheet) -->
    <BookingSheet v-if="!isOffline" />

    <!-- Mobile bottom bar: sticky booking CTA (legacy 5-tab nav behind a flag) -->
    <template v-if="!isOffline">
      <BottomNav v-if="legacyBottomNav" />
      <StickyCtaBar v-else />
    </template>
  </div>
</template>

<script>
import { defineAsyncComponent, watchEffect } from 'vue'
import { useTenantConfig } from '@/composables/useTenantConfig'
import { useThemeTokens } from '@/composables/useThemeTokens'
import { useBusinessSettings } from '@/composables/useBusinessSettings'
import AppHeader from './components/Header.vue'
import HeroSection from './components/HeroSection.vue'
import ServicesSection from './components/ServicesSection.vue'
import TrustStrip from './components/TrustStrip.vue'
import AtmosphereSection from './components/AtmosphereSection.vue'
import VisitUsSection from './components/VisitUsSection.vue'
import FaqSection from './components/FaqSection.vue'
import AppFooter from './components/Footer.vue'
import OfflinePage from './components/OfflinePage.vue'
import BottomNav from './components/BottomNav.vue'
import StickyCtaBar from './components/StickyCtaBar.vue'
// Lazy load modals for better performance
const ImpressumModal = defineAsyncComponent(() => import('./components/ImpressumModal.vue'))
const DataProtectionModal = defineAsyncComponent(() => import('./components/DataProtectionModal.vue'))
const TermsModal = defineAsyncComponent(() => import('./components/TermsModal.vue'))
const AnnouncementModal = defineAsyncComponent(() => import('./components/AnnouncementModal.vue'))
const BookingSheet = defineAsyncComponent(() => import('./components/BookingSheet.vue'))
// Eager load CancellationModal to prevent race condition when users click cancellation links
import CancellationModal from './components/CancellationModal.vue'
import { useGoogleAds } from '@/composables/useGoogleAds'
import { useBookingSheet } from '@/composables/useBookingSheet'
import { setToken } from '@/utils/thankYouAttribution'

// In-page section ids only (#home, #services, ...). Anything else, e.g. the
// thank-you fragment #ty=<token>, is never passed to querySelector.
const SECTION_HASH_RE = /^#[A-Za-z][A-Za-z0-9_-]*$/

export default {
  setup() {
    // Token layer: written to <html> so teleported UI (outside #app) can read them.
    const { cssVars } = useThemeTokens();
    watchEffect(() => {
      const root = document.documentElement;
      for (const [k, v] of Object.entries(cssVars.value)) root.style.setProperty(k, v);
    });
    const {
      businessName,
      businessMotto,
      showHeroText,
      showLogo,
      load: loadBusinessSettings
    } = useBusinessSettings();
    return {
      businessName,
      businessMotto,
      showHeroText,
      showLogo,
      loadBusinessSettings
    };
  },
  name: 'App',
  components: {
    AppHeader,
    HeroSection,
    ServicesSection,
    TrustStrip,
    AtmosphereSection,
    VisitUsSection,
    FaqSection,
    AppFooter,
    OfflinePage,
    BottomNav,
    StickyCtaBar,
    ImpressumModal,
    DataProtectionModal,
    TermsModal,
    CancellationModal,
    AnnouncementModal,
    BookingSheet
  },
  data() {
    return {
      envBanner: false,
      showImpressum: false,
      showDataProtection: false,
      showTerms: false,
      showCancellationModal: false,
      cancellationBookingId: null,
      // Thank-you email auto-open, deferred until the announcement modal is
      // known not to show or has been dismissed (never both overlays at once).
      pendingThankYouSheet: false,
      isWidgetExpanded: false
    }
  },
  computed: {
    // Per-tenant "site offline" switch (inside.zabaiherz -> tenant-config `offline`).
    // Only a literal true counts; an older backend without the key renders the page.
    isOffline() {
      return useTenantConfig().config.value?.offline === true
    },
    // The owner's optional notice, shown verbatim on the offline page.
    offlineMessage() {
      const msg = useTenantConfig().config.value?.offlineMessage
      return typeof msg === 'string' ? msg.trim() : ''
    },
    // Legacy 5-tab BottomNav for one release: tenant-config opt-in plus a
    // localStorage override for QA. Defaults to the new sticky CTA bar.
    legacyBottomNav() {
      if (useTenantConfig().config.value?.ui?.bottomNav === 'legacy') return true
      try {
        return window.localStorage.getItem('zh_ui_bottomnav') === 'legacy'
      } catch {
        return false
      }
    }
  },
  created() {
    // Must run first: strips the thank-you token (#ty=) and ?src=ty from the URL
    // before hash navigation reads it and before Google Ads (mounted) loads gtag,
    // which reads the page URL.
    this.captureThankYouToken();
    this.checkForLegalRequest();
  },
  mounted() {
    const tenantTag = useTenantConfig().config.value?.tag;
    this.envBanner = tenantTag === 'stage' || tenantTag === 'dev';
    this.applyTenantMeta();
    this.checkForCancellationRequest();
    this.loadBusinessSettings().then(() => this.updatePageTitle());
    this.handleHashNavigation();
    window.addEventListener('hashchange', this.handleHashNavigation);

    // No ads tag while offline: nothing on the page can convert.
    if (!this.isOffline) {
      const { initializeGoogleAds } = useGoogleAds();
      initializeGoogleAds();
    }

    this._revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
            this._revealObserver.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.1 }
    )
    this.$el.querySelectorAll('.reveal-wrap').forEach(el => this._revealObserver.observe(el))
  },

  beforeUnmount() {
    window.removeEventListener('hashchange', this.handleHashNavigation);
    if (this._revealObserver) this._revealObserver.disconnect()
  },
  methods: {
    updatePageTitle() {
      // Set the browser tab title to the business name (keep the tenant-config
      // title when business settings are unavailable)
      if (this.businessName) document.title = this.businessName;
    },

    applyTenantMeta() {
      const cfg = useTenantConfig().config.value;
      if (!cfg) return;
      const set = (sel, attr, val) => { if (val) document.querySelector(sel)?.setAttribute(attr, val); };
      // <html lang> follows the rendered locale (set in main.js), not the tenant's content
      // source language (cfg.primaryLanguage), which would mislabel e.g. an English page as German.
      set('meta[name="description"]', 'content', cfg.metaDescription);
      set('meta[name="theme-color"]', 'content', cfg.themeColor);
      set('meta[property="og:site_name"]', 'content', cfg.siteName);
      set('meta[property="og:title"]', 'content', cfg.siteTitle);
      set('meta[property="og:description"]', 'content', cfg.ogDescription);
      set('meta[property="og:url"]', 'content', window.location.origin);
      set('link[rel="canonical"]', 'href', window.location.origin);
      const ogImage = cfg.ogImage || cfg.images?.banner;
      if (ogImage) {
        const abs = ogImage.startsWith('http') ? ogImage : window.location.origin + ogImage;
        set('meta[property="og:image"]', 'content', abs);
      }
      if (cfg.siteName) document.title = cfg.siteName;
    },

    checkForCancellationRequest() {
      // Check URL parameters for cancellation request
      const urlParams = new URLSearchParams(window.location.search);
      const cancelBookingId = urlParams.get('cancel');
      
      if (cancelBookingId) {
        console.log('Cancellation request detected for booking:', cancelBookingId);
        this.cancellationBookingId = cancelBookingId;
        this.showCancellationModal = true;
        
        // Clean up the URL without page reload
        const url = new URL(window.location);
        url.searchParams.delete('cancel');
        window.history.replaceState({}, document.title, url.pathname + url.hash);
      }
    },

    // Rebook link from the thank-you email lands on /?src=ty#ty=<token>.
    // The token is kept in memory only (utils/thankYouAttribution.js).
    captureThankYouToken() {
      const url = new URL(window.location.href);
      const fromThankYouEmail = url.searchParams.get('src') === 'ty';
      const hasTokenHash = url.hash.startsWith('#ty=');
      if (!fromThankYouEmail && !hasTokenHash) return;

      if (hasTokenHash) {
        setToken(new URLSearchParams(url.hash.slice(1)).get('ty'));
        url.hash = '';
      }
      if (fromThankYouEmail) url.searchParams.delete('src');
      window.history.replaceState(window.history.state, document.title, url.pathname + url.search + url.hash);

      // Open the booking sheet at its first step (never over a cancellation link).
      // Deferred: AnnouncementModal decides after its fetch whether it shows, and
      // the sheet opens from onAnnouncementResolved / onAnnouncementClosed.
      // Nothing to open while the site is offline (the sheet is not rendered).
      if (fromThankYouEmail && !url.searchParams.has('cancel') && !this.isOffline) {
        this.pendingThankYouSheet = true;
      }
    },

    // Consumes the deferred thank-you auto-open exactly once.
    openPendingThankYouSheet() {
      if (!this.pendingThankYouSheet) return;
      this.pendingThankYouSheet = false;
      const sheet = useBookingSheet();
      // open() restarts the flow; don't reset a booking the customer already started.
      if (!sheet.isOpen.value) sheet.open({ source: 'thank-you-email' });
    },

    // /?legal=privacy (linked from email footers) opens the data protection notice.
    checkForLegalRequest() {
      const url = new URL(window.location.href);
      if (url.searchParams.get('legal') !== 'privacy') return;
      this.showDataProtection = true;
      url.searchParams.delete('legal');
      window.history.replaceState(window.history.state, document.title, url.pathname + url.search + url.hash);
    },

    onAnnouncementResolved(willShow) {
      if (!willShow) this.openPendingThankYouSheet();
    },

    onAnnouncementClosed() {
      console.log('📢 Announcement modal closed');
      this.openPendingThankYouSheet();
    },

    closeCancellationModal() {
      this.showCancellationModal = false;
      this.cancellationBookingId = null;
    },
    handleHashNavigation() {
      this.$nextTick(() => {
        const hash = window.location.hash;
        if (hash && SECTION_HASH_RE.test(hash)) {
          setTimeout(() => {
            const element = document.querySelector(hash);
            if (element) {
              // Scroll with offset for sticky header
              const headerOffset = 80; // Adjust based on your header height
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
            }
          }, 100);
        }
      });
    },
    handleWidgetMessage(event) {
        if (event.data && event.data.type === 'resize-widget') {
            this.isWidgetExpanded = event.data.expanded;
        }
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* Layout / type primitives (theme colour tokens are set on <html> from App.vue setup) */
:root {
  --gutter: clamp(16px, 4vw, 32px);
  --s-1: 4px;
  --s-2: 8px;
  --s-3: 12px;
  --s-4: 16px;
  --s-5: 24px;
  --s-6: 32px;
  --s-7: 48px;
  --s-8: 64px;
  --fs-display: clamp(2rem, 1.2rem + 3.5vw, 3.75rem);
  --fs-h2: clamp(1.625rem, 1.1rem + 2vw, 2.5rem);
  --fs-h3: clamp(1.125rem, 1rem + .8vw, 1.5rem);
  --fs-body: clamp(1rem, .95rem + .25vw, 1.0625rem);
  --fs-price: clamp(2rem, 1.5rem + 2vw, 3rem);
  --r-1: 8px;
  --r-2: 12px;
  --r-3: 16px;
}

html {
  color-scheme: var(--scheme, light);
}

.env-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: #f59e0b;
  color: #1c1c1c;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 8px 1rem;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

#app.has-env-banner {
  padding-top: 36px;
  --banner-h: 36px;
}

#app {
  font-family: 'Nunito', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--page-background);
}

/* Playfair Display for all headings */
h1, h2, h3, h4 {
  font-family: 'Playfair Display', serif;
  letter-spacing: 0.01em;
}

main {
  flex: 1;
}



.section {
  padding: 4rem 2rem;
  text-align: center;
  max-width: 1200px;
  margin: 0 auto;
}

/* ── Scroll reveal ── */
.reveal-wrap {
  opacity: 0;
  transform: translateY(32px);
  transition:
    opacity  0.65s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.65s cubic-bezier(0.22, 1, 0.36, 1);
}

.reveal-wrap.visible {
  opacity: 1;
  transform: translateY(0);
}

/* Respect user's motion preference */
@media (prefers-reduced-motion: reduce) {
  .reveal-wrap {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

@media (max-width: 768px) {
  .section h2 {
    font-size: 2rem;
  }

  /* Room for the fixed bottom nav (footer handles its own via Footer.vue) */
  main {
    padding-bottom: calc(64px + env(safe-area-inset-bottom));
  }

  /* Offline: no sticky CTA bar, so neither main nor the footer needs to clear one. */
  #app.is-offline main {
    padding-bottom: 0;
  }

  #app.is-offline .footer {
    padding-bottom: var(--s-4);
  }
}
</style>
